import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useTableNavigation } from '../hooks/useTableNavigation';
import { useDraft } from '../hooks/useDraft';
import DraftStatusBadge from './DraftStatusBadge';

import { createEmptyRow, getSizeGroups, sortScannedRows, computeTotals } from './create-purchase-return/helpers';
import PRHeader from './create-purchase-return/PRHeader';
import StockErrorBanner from './create-purchase-return/StockErrorBanner';
import VendorDetailsCard from './create-purchase-return/VendorDetailsCard';
import PRScannerCard from './create-purchase-return/PRScannerCard';
import ReturnTable from './create-purchase-return/ReturnTable';
import FreightFooter from './create-purchase-return/FreightFooter';

const CreatePurchaseReturn = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editPR = location.state?.editPR || null;
  const prefillFromDefect = location.state?.prefillVendorId || null;
  const prefillDefectItems = location.state?.prefillItems || null;

  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [enums, setEnums] = useState({ sizes: [], colors: [] });

  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [debitNoteNumber, setDebitNoteNumber] = useState('');
  const [returnNotes, setReturnNotes] = useState('');

  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [saving, setSaving] = useState(false);

  // Draft auto-save
  const { draftStatus, save: saveDraftData, clear: clearDraft } = useDraft('PR', { enabled: !editPR, initialDraftId: location.state?.draftId || null });

  const [freightCharges, setFreightCharges] = useState('');
  const [currentBoxNumber, setCurrentBoxNumber] = useState(1);
  const [breakPopupIdx, setBreakPopupIdx] = useState(null);
  const [vendorDesigns, setVendorDesigns] = useState([]);
  const [stockError, setStockError] = useState(null);

  const vendorRef = useRef(null);
  const handleTableKeyDown = useTableNavigation('pr-invoice-table');
  const currentBoxRef = useRef(1);
  useEffect(() => { currentBoxRef.current = currentBoxNumber; }, [currentBoxNumber]);

  // Scanner state
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = 'pr-qr-reader';
  const productsRef = useRef([]);
  const rowsRef = useRef(rows);
  const selectedVendorRef = useRef(selectedVendor);
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { selectedVendorRef.current = selectedVendor; }, [selectedVendor]);

  // Fetch vendor-supplied designs when vendor changes
  useEffect(() => {
    if (!selectedVendor) { setVendorDesigns([]); return; }
    const fetchVendorDesigns = async () => {
      try {
        const res = await api.get(`/purchase-returns/vendor-designs/${selectedVendor}`);
        setVendorDesigns(res.data || []);
      } catch { setVendorDesigns([]); }
    };
    fetchVendorDesigns();
  }, [selectedVendor]);

  // Scanner: handle scanned code
  const handleScannedCode = useCallback(async (code) => {
    const prods = productsRef.current;
    const curRows = rowsRef.current;
    let scanned = code.trim().toUpperCase();
    try { const qr = JSON.parse(code.trim()); if (qr.design) scanned = qr.design.toUpperCase(); } catch {}
    let product = prods.find(p => p.design_code?.toUpperCase() === scanned);
    let matchedSku = null;
    if (!product) { for (const p of prods) { const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === scanned); if (sku) { product = p; matchedSku = sku; break; } } }
    if (!product && (scanned.includes('_') || scanned.includes('-'))) { const parts = scanned.split(/[_-]/); if (parts.length >= 2) product = prods.find(p => p.design_code?.toUpperCase() === parts[0]); }
    if (!product) { toast.error(`Design "${scanned}" not found`); return; }

    const skuColors = (product.skus || []).map(s => s.color).filter(Boolean);
    const imgColors = Array.isArray(product.image_colors) ? product.image_colors : (product.image_colors && typeof product.image_colors === 'object' ? Object.values(product.image_colors).filter(Boolean) : []);
    const colors = [...new Set([...skuColors, ...imgColors])];
    const sizes = [...new Set(product.skus.map(s => s.size))];
    let sourceBill = '', sourceBillDate = '', sourceEntryId = '', peVariants = [];
    try {
      const params = selectedVendorRef.current ? `?vendor_id=${selectedVendorRef.current}` : '';
      const res = await api.get(`/purchase-returns/lookup-design/${product.design_code}${params}`);
      if (res.data.found) { sourceBill = res.data.invoice_number || res.data.challan_number || res.data.grn_number || ''; sourceBillDate = res.data.entry_date || ''; sourceEntryId = res.data.entry_id || ''; peVariants = res.data.pe_variants || []; }
    } catch {}
    const emptyIdx = curRows.findIndex(r => !r.product_id);
    setRows(prev => {
      if (emptyIdx >= 0) {
        const next = [...prev];
        next[emptyIdx] = { ...next[emptyIdx], product_id: product.id, design_code: product.design_code, designSearch: product.design_code, colors, sizes, product, showDesignDropdown: false, color: matchedSku?.color || '', size: matchedSku?.size || '', source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId, pe_variants: peVariants };
        return next;
      }
      return [...prev, { ...createEmptyRow(prev.length + 1), product_id: product.id, design_code: product.design_code, designSearch: product.design_code, colors, sizes, product, color: matchedSku?.color || '', size: matchedSku?.size || '', source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId, pe_variants: peVariants, box_number: currentBoxRef.current }];
    });
    toast.success(`Scanned: ${product.design_code}${sourceBill ? ` (Bill: ${sourceBill})` : ''}`);
  }, []);

  const startScanner = useCallback(async () => {
    setScannerActive(true);
    const { Html5Qrcode } = await import('html5-qrcode');
    setTimeout(() => {
      const el = document.getElementById(scannerContainerId);
      if (!el) { setScannerActive(false); return; }
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 2.0 }, (t) => handleScannedCode(t), () => {}).catch(() => { toast.error('Camera not available. Use wireless scanner.'); setScannerActive(false); });
    }, 200);
  }, [handleScannedCode]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) { try { scannerRef.current.stop().then(() => { try { scannerRef.current.clear(); } catch(e) {} }).catch(() => {}); } catch(e) {} scannerRef.current = null; }
    setScannerActive(false);
  }, []);

  useEffect(() => { return () => { if (scannerRef.current) { try { scannerRef.current.stop().catch(() => {}); } catch(e) {} scannerRef.current = null; } }; }, []);

  const [dataLoaded, setDataLoaded] = useState(false);
  useEffect(() => {
    if (dataLoaded) {
      setTimeout(() => { const input = document.querySelector('[data-testid="pr-design-search-0"]'); if (input) input.focus(); }, 300);
    }
  }, [dataLoaded]);

  useEffect(() => {
    const handleClick = (e) => {
      if (vendorRef.current && !vendorRef.current.contains(e.target)) setVendorDropdownOpen(false);
      if (!e.target.closest('[data-testid^="pr-break"]')) setBreakPopupIdx(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close design dropdowns when clicking outside any design cell
  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.closest && e.target.closest('[data-pr-design-cell]')) return;
      setRows(prev => prev.map(r => (r.showDesignDropdown ? { ...r, showDesignDropdown: false } : r)));
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [vendRes, prodRes, enumRes] = await Promise.all([
          api.get('/vendors'), api.get('/products'), api.get('/enums'),
        ]);
        setVendors(vendRes.data);
        setProducts(prodRes.data);
        setEnums(enumRes.data);

        if (!editPR) {
          const dnRes = await api.get('/purchase-returns/next-debit-note');
          setDebitNoteNumber(dnRes.data.debit_note_number);
        }

        if (editPR) prefillEdit(vendRes.data, prodRes.data, editPR);
        else if (prefillFromDefect && prefillDefectItems) prefillFromDefectGoods(vendRes.data, prodRes.data);
        setDataLoaded(true);
      } catch { toast.error('Failed to load data'); }
    };
    load();
  }, []);

  const prefillEdit = (vends, prods, pr) => {
    const vend = vends.find(v => v.id === pr.vendor_id);
    if (vend) {
      setSelectedVendor(vend.id);
      setVendorSearch(vend.name + (vend.city ? ` (${vend.city})` : ''));
    }
    setReturnDate(pr.return_date || new Date().toISOString().split('T')[0]);
    setDebitNoteNumber(pr.debit_note_number || '');
    setReturnNotes(pr.notes || '');
    setFreightCharges(pr.freight_charges || '');

    const editRows = [];
    let sr = 1;
    for (const item of (pr.items || [])) {
      const prod = prods.find(p => p.id === item.product_id);
      const skuC = prod ? (prod.skus || []).map(s => s.color).filter(Boolean) : [];
      const imgC = prod ? (Array.isArray(prod.image_colors) ? prod.image_colors : (prod.image_colors && typeof prod.image_colors === 'object' ? Object.values(prod.image_colors).filter(Boolean) : [])) : [];
      const colors = [...new Set([...skuC, ...imgC])];
      const sizes = prod ? [...new Set((prod.skus || []).map(s => s.size))] : [];
      // Merge variants with same color+size (handles duplicate entries)
      const variantMap = {};
      for (const v of (item.variants || [])) {
        const vKey = `${v.color}_${v.size}`;
        if (variantMap[vKey]) {
          variantMap[vKey].qty_returned = (variantMap[vKey].qty_returned || 0) + (v.qty_returned || 0);
        } else {
          variantMap[vKey] = { ...v };
        }
      }
      for (const v of Object.values(variantMap)) {
        // Build size groups/individual sizes for the edit row
        const peVars = (item.pe_variants || item.variants || []).filter(pv => pv.color === v.color);
        let sizeGroups = [], individualSizes = [];
        if (peVars.length > 0) {
          const byPrice = {};
          peVars.forEach(pv => {
            const price = pv.cost || pv.rate || 0;
            if (!byPrice[price]) byPrice[price] = [];
            byPrice[price].push(pv.size);
          });
          sizeGroups = Object.entries(byPrice)
            .filter(([, szs]) => szs.length > 1)
            .map(([price, szs]) => ({
              id: `set_${szs[0]}_${szs[szs.length - 1]}`,
              label: `Set: ${szs[0]}-${szs[szs.length - 1]}`,
              sizes: szs, rate: parseFloat(price), count: szs.length,
            }));
          individualSizes = peVars.map(pv => ({ size: pv.size, rate: pv.cost || pv.rate || 0 }));
        } else if (prod) {
          const r = getSizeGroups(prod, v.color);
          sizeGroups = r.groups; individualSizes = r.individualSizes;
        }
        editRows.push({
          sr, product_id: item.product_id, design_code: item.design_code,
          designSearch: item.design_code, color: v.color, size: v.size,
          qty: v.qty_returned || 0, reason: v.reason || 'defective', notes: v.notes || '',
          rate: v.rate || v.cost || '', box_number: v.box_number || 1,
          colors, sizes, product: prod, showDesignDropdown: false,
          sizeGroups, individualSizes,
          source_bill: item.source_grn || '', source_bill_date: '',
          source_entry_id: item.source_entry_id || '',
        });
        sr++;
      }
    }
    if (editRows.length === 0) editRows.push(createEmptyRow(1));
    setRows(editRows);
  };

  const prefillFromDefectGoods = async (vends, prods) => {
    // Set vendor
    const vend = vends.find(v => v.id === prefillFromDefect);
    if (vend) {
      setSelectedVendor(vend.id);
      setVendorSearch(vend.name + (vend.city ? ` (${vend.city})` : ''));
    }
    // Build rows from defect items — call PE lookup for each design to get full data
    const defectRows = [];
    let sr = 1;
    const lookupCache = {};
    for (const di of prefillDefectItems) {
      const prod = prods.find(p => p.id === di.product_id);
      const allColors = prod ? [...new Set([...(prod.skus || []).map(s => s.color).filter(Boolean), ...(Array.isArray(prod.image_colors) ? prod.image_colors : (prod.image_colors && typeof prod.image_colors === 'object' ? Object.values(prod.image_colors).filter(Boolean) : []))])] : [];
      const sizes = prod ? [...new Set((prod.skus || []).map(s => s.size))] : [];

      // Fetch PE lookup data (cache by design_code)
      let peData = lookupCache[di.design_code];
      if (!peData) {
        try {
          const params = prefillFromDefect ? `?vendor_id=${prefillFromDefect}` : '';
          const res = await api.get(`/purchase-returns/lookup-design/${di.design_code}${params}`);
          peData = res.data;
          lookupCache[di.design_code] = peData;
        } catch { peData = { found: false }; }
      }

      const peVariants = peData?.pe_variants || [];
      const sourceBill = peData?.found ? (peData.invoice_number || peData.challan_number || peData.grn_number || '') : '';
      const sourceBillDate = peData?.found ? (peData.entry_date || '') : '';
      const sourceEntryId = peData?.found ? (peData.entry_id || '') : '';

      // Auto-fill rate from PE variant
      let rate = 0;
      const peVar = peVariants.find(v => v.color === di.color && v.size === di.size);
      if (peVar) rate = peVar.cost || 0;
      else {
        const peVarColor = peVariants.find(v => v.color === di.color);
        if (peVarColor) rate = peVarColor.cost || 0;
      }

      // Build size groups from PE variants for this color
      const peColorVars = peVariants.filter(v => v.color === di.color);
      let sizeGroups = [], individualSizes = [];
      if (peColorVars.length > 0) {
        const byPrice = {};
        peColorVars.forEach(v => {
          const price = v.cost || 0;
          if (!byPrice[price]) byPrice[price] = [];
          byPrice[price].push(v.size);
        });
        sizeGroups = Object.entries(byPrice)
          .filter(([, szs]) => szs.length > 1)
          .map(([price, szs]) => ({
            id: `set_${szs[0]}_${szs[szs.length - 1]}`,
            label: `Set: ${szs[0]}-${szs[szs.length - 1]}`,
            sizes: szs, rate: parseFloat(price), count: szs.length,
          }));
        individualSizes = peColorVars.map(v => ({ size: v.size, rate: v.cost || 0 }));
      } else if (prod) {
        const r = getSizeGroups(prod, di.color);
        sizeGroups = r.groups; individualSizes = r.individualSizes;
      }

      // Filter colors to PE history
      const peColors = [...new Set(peVariants.map(v => v.color))];
      const colors = peColors.length > 0 ? allColors.filter(c => peColors.includes(c)) : allColors;

      defectRows.push({
        sr, product_id: di.product_id, design_code: di.design_code,
        designSearch: di.design_code, color: di.color, size: di.size,
        qty: di.qty || 0, reason: di.reason || 'defective', notes: '',
        rate, box_number: 1,
        colors, sizes, product: prod, showDesignDropdown: false,
        sizeGroups, individualSizes, pe_variants: peVariants,
        source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId,
      });
      sr++;
    }
    if (defectRows.length === 0) defectRows.push(createEmptyRow(1));
    setRows(defectRows);
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor.id);
    setVendorSearch(vendor.name + (vendor.city ? ` (${vendor.city})` : ''));
    setVendorDropdownOpen(false);
  };

  const filteredVendors = useMemo(() => {
    if (!vendorSearch.trim()) return vendors.slice(0, 20);
    const q = vendorSearch.trim().toLowerCase();
    return vendors.filter(v =>
      v.name?.toLowerCase().includes(q) || v.city?.toLowerCase().includes(q) || (/\d/.test(vendorSearch) && v.phone?.includes(q))
    ).slice(0, 20);
  }, [vendorSearch, vendors]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx] };
      row[field] = value;

      // SKU scan detection
      if (field === 'designSearch' && value && value.length >= 3) {
        const searchUpper = value.trim().toUpperCase();
        let matchedProduct = null, matchedSku = null;
        for (const p of products) { const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === searchUpper); if (sku) { matchedProduct = p; matchedSku = sku; break; } }
        if (!matchedProduct && (searchUpper.includes('_') || searchUpper.includes('-'))) { const parts = searchUpper.split(/[_-]/); if (parts.length >= 2) { matchedProduct = products.find(p => p.design_code?.toUpperCase() === parts[0]); if (matchedProduct) matchedSku = matchedProduct.skus?.find(s => s.sku_code?.toUpperCase() === searchUpper) || null; } }
        if (matchedProduct && matchedSku) {
          const existingIdx = next.findIndex((r, i) => i !== idx && r.product_id === matchedProduct.id && r.color === matchedSku.color && r.size === matchedSku.size);
          if (existingIdx >= 0) {
            const existing = { ...next[existingIdx] };
            existing.qty = (parseFloat(existing.qty) || 0) + 1;
            next[existingIdx] = existing;
            row.designSearch = '';
            next[idx] = row;
            toast.success(`${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size} → Qty: ${existing.qty}`);
            const sorted = sortScannedRows(next);
            setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="pr-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
            return sorted;
          }
          const colors = [...new Set(matchedProduct.skus.map(s => s.color))];
          const sizes = [...new Set(matchedProduct.skus.map(s => s.size))];
          row.product_id = matchedProduct.id; row.design_code = matchedProduct.design_code; row.designSearch = matchedProduct.design_code;
          row.colors = colors; row.sizes = sizes; row.product = matchedProduct; row.showDesignDropdown = false;
          row.color = matchedSku.color; row.size = matchedSku.size; row.qty = 1; row.reason = 'defective';
          next[idx] = row;
          toast.success(`Scanned: ${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size}`);
          next.push(createEmptyRow(next.length + 1));
          const sorted = sortScannedRows(next);
          setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="pr-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
          return sorted;
        }
      }

      if (field === 'designSearch' && row.design_code && value !== row.design_code) {
        row.product_id = ''; row.design_code = ''; row.color = ''; row.size = '';
        row.colors = []; row.sizes = []; row.sizeGroups = []; row.individualSizes = [];
        row.product = null; row.qty = ''; row.pe_variants = [];
      }
      if (field === 'color' && row.product) {
        // Use PE variant sizes if available, otherwise fall back to product master
        const peColorVars = (row.pe_variants || []).filter(v => v.color === value);
        let groups, individualSizes;
        if (peColorVars.length > 0) {
          const byPrice = {};
          peColorVars.forEach(v => {
            const price = v.cost || 0;
            if (!byPrice[price]) byPrice[price] = [];
            byPrice[price].push(v.size);
          });
          groups = Object.entries(byPrice)
            .filter(([, sizes]) => sizes.length > 1)
            .map(([price, sizes]) => ({
              id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
              label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
              sizes, rate: parseFloat(price), count: sizes.length,
            }));
          individualSizes = peColorVars.map(v => ({ size: v.size, rate: v.cost || 0 }));
        } else {
          ({ groups, individualSizes } = getSizeGroups(row.product, value));
        }
        row.sizeGroups = groups;
        row.individualSizes = individualSizes;
        row.sizes = individualSizes.map(s => s.size);
        row.rate = 0;
        // Auto-fill rate from PE cost
        const peVar = peColorVars.length > 0 ? peColorVars[0] : null;
        if (peVar && peVar.cost > 0) {
          row.rate = peVar.cost;
        } else if (groups.length > 0) {
          row.rate = groups[0].rate || 0;
        } else if (individualSizes.length > 0) {
          row.rate = individualSizes[0].rate || 0;
        }
        if (groups.length > 0) {
          row.size = groups[0].id;
        } else if (individualSizes.length > 0) {
          row.size = individualSizes[0].size;
        }
      }
      if (field === 'size' && row.product) {
        // Check PE cost for specific size
        const peVar = (row.pe_variants || []).find(v => v.color === row.color && v.size === value);
        if (peVar && peVar.cost > 0) {
          row.rate = peVar.cost;
        } else if (value === 'both') {
          const allRates = (row.sizeGroups || []).map(g => g.rate || 0);
          row.rate = allRates.length > 0 ? allRates[0] : 0;
        } else {
          const grp = (row.sizeGroups || []).find(g => g.id === value);
          if (grp) {
            row.rate = grp.rate || 0;
          } else {
            const ind = (row.individualSizes || []).find(s => s.size === value);
            row.rate = ind ? ind.rate || 0 : 0;
          }
        }
      }
      next[idx] = row;
      return next;
    });
  };

  const selectDesign = async (idx, product) => {
    const skuC2 = (product.skus || []).map(s => s.color).filter(Boolean);
    const imgC2 = Array.isArray(product.image_colors) ? product.image_colors : (product.image_colors && typeof product.image_colors === 'object' ? Object.values(product.image_colors).filter(Boolean) : []);
    const allColors = [...new Set([...skuC2, ...imgC2])];
    const sizes = [...new Set((product.skus || []).map(s => s.size))];

    let sourceBill = '', sourceBillDate = '', sourceEntryId = '', peVariants = [];
    try {
      const params = selectedVendor ? `?vendor_id=${selectedVendor}` : '';
      const res = await api.get(`/purchase-returns/lookup-design/${product.design_code}${params}`);
      if (res.data.found) {
        sourceBill = res.data.invoice_number || res.data.challan_number || res.data.grn_number || '';
        sourceBillDate = res.data.entry_date || '';
        sourceEntryId = res.data.entry_id || '';
        peVariants = res.data.pe_variants || [];
      }
    } catch { /* ignore */ }

    // Filter colors to only those purchased in PE
    const peColors = [...new Set(peVariants.map(v => v.color))];
    const colors = peColors.length > 0 ? allColors.filter(c => peColors.includes(c)) : allColors;

    // Auto-select color if only one, and cascade to size/rate
    let autoColor = '', autoSizeGroups = [], autoIndividualSizes = [], autoSize = '', autoRate = 0;
    if (colors.length === 1) {
      autoColor = colors[0];
      // Use PE variant sizes if available
      const peColorVars = peVariants.filter(v => v.color === autoColor);
      if (peColorVars.length > 0) {
        const byPrice = {};
        peColorVars.forEach(v => {
          const price = v.cost || 0;
          if (!byPrice[price]) byPrice[price] = [];
          byPrice[price].push(v.size);
        });
        autoSizeGroups = Object.entries(byPrice)
          .filter(([, sizes]) => sizes.length > 1)
          .map(([price, sizes]) => ({
            id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
            label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
            sizes, rate: parseFloat(price), count: sizes.length,
          }));
        autoIndividualSizes = peColorVars.map(v => ({ size: v.size, rate: v.cost || 0 }));
      } else {
        const { groups, individualSizes } = getSizeGroups(product, autoColor);
        autoSizeGroups = groups;
        autoIndividualSizes = individualSizes;
      }
      // Auto-fill rate from PE cost
      const peVar = peVariants.find(v => v.color === autoColor);
      if (peVar && peVar.cost > 0) {
        autoRate = peVar.cost;
      } else if (autoSizeGroups.length > 0) {
        autoRate = autoSizeGroups[0].rate || 0;
      } else if (autoIndividualSizes.length > 0) {
        autoRate = autoIndividualSizes[0].rate || 0;
      }
      if (autoSizeGroups.length > 0) autoSize = autoSizeGroups[0].id;
      else if (autoIndividualSizes.length > 0) autoSize = autoIndividualSizes[0].size;
    }

    setRows(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, sizes, product,
        showDesignDropdown: false,
        color: autoColor, size: autoSize, rate: autoRate,
        sizeGroups: autoSizeGroups, individualSizes: autoIndividualSizes,
        source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId,
        pe_variants: peVariants,
      };
      return next;
    });
    // Focus colour dropdown after selection if not auto-set
    if (!autoColor) {
      setTimeout(() => {
        const colorEl = document.querySelector(`[data-testid="pr-color-${idx}"]`);
        if (colorEl) colorEl.focus();
      }, 100);
    }
  };

  const addRow = () => setRows(prev => [...prev, { ...createEmptyRow(prev.length + 1), box_number: currentBoxNumber }]);
  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sr: i + 1 })));
  };

  const handleBreak = () => {
    setBreakPopupIdx(null);
    setCurrentBoxNumber(prev => prev + 1);
    toast.success(`Starting Box ${currentBoxNumber + 1}`);
  };

  const handleUndoBreak = () => {
    if (currentBoxNumber <= 1) return;
    const lastBox = currentBoxNumber;
    setRows(prev => prev.map(r => r.box_number === lastBox ? { ...r, box_number: lastBox - 1 } : r));
    setCurrentBoxNumber(prev => prev - 1);
    toast.success(`Merged Box ${lastBox} back into Box ${lastBox - 1}`);
  };

  // Grouped rows by box
  const groupedBoxRows = useMemo(() => {
    const boxes = {};
    rows.forEach((r, idx) => {
      const bn = r.box_number || 1;
      if (!boxes[bn]) boxes[bn] = [];
      boxes[bn].push({ ...r, _idx: idx });
    });
    return boxes;
  }, [rows]);
  const hasMultipleBoxes = Object.keys(groupedBoxRows).length > 1;

  const filteredDesigns = (search) => {
    let pool = products;
    // Filter to vendor-supplied designs when a vendor is selected
    if (selectedVendor && vendorDesigns.length > 0) {
      const vdSet = new Set(vendorDesigns.map(d => d.toLowerCase()));
      pool = products.filter(p => vdSet.has(p.design_code.toLowerCase()));
    }
    if (!search.trim()) return pool.slice(0, 15);
    const q = search.trim().toLowerCase();
    return pool.filter(p => p.design_code.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q)).slice(0, 15);
  };

  const submitReturn = async (forceOverride = false) => {
    if (!selectedVendor) { toast.error('Select a vendor'); return; }
    const validRows = rows.filter(r => r.product_id && r.color && r.size && parseInt(r.qty) > 0);
    if (validRows.length === 0) { toast.error('Add at least one item with color, size, and qty'); return; }

    const itemsMap = {};
    for (const r of validRows) {
      const key = `${r.product_id}_${r.source_entry_id || 'none'}`;
      if (!itemsMap[key]) {
        itemsMap[key] = {
          product_id: r.product_id, design_code: r.design_code,
          source_entry_id: r.source_entry_id || null,
          source_grn: r.source_bill || null,
          box_number: r.box_number || 1,
          variants: [],
        };
      }
      const totalQty = parseInt(r.qty) || 0;
      if (r.size === 'both') {
        (r.sizeGroups || []).forEach(grp => {
          grp.sizes.forEach(size => {
            if (totalQty > 0) {
              itemsMap[key].variants.push({
                size, color: r.color, qty_returned: totalQty,
                reason: r.reason || 'defective', notes: r.notes || null,
                box_number: r.box_number || 1, rate: parseFloat(r.rate) || 0,
              });
            }
          });
        });
      } else {
        const grp = (r.sizeGroups || []).find(g => g.id === r.size);
        if (grp) {
          grp.sizes.forEach(size => {
            if (totalQty > 0) {
              itemsMap[key].variants.push({
                size, color: r.color, qty_returned: totalQty,
                reason: r.reason || 'defective', notes: r.notes || null,
                box_number: r.box_number || 1, rate: parseFloat(r.rate) || 0,
              });
            }
          });
        } else if (r.size && totalQty > 0) {
          itemsMap[key].variants.push({
            size: r.size, color: r.color, qty_returned: totalQty,
            reason: r.reason || 'defective', notes: r.notes || null,
            box_number: r.box_number || 1, rate: parseFloat(r.rate) || 0,
          });
        }
      }
    }

    // Deduplicate variants: merge same size+color by summing qty
    for (const item of Object.values(itemsMap)) {
      const vMap = {};
      for (const v of item.variants) {
        const vKey = `${v.size}_${v.color}`;
        if (vMap[vKey]) {
          vMap[vKey].qty_returned += v.qty_returned;
        } else {
          vMap[vKey] = { ...v };
        }
      }
      item.variants = Object.values(vMap);
    }

    const payload = {
      vendor_id: selectedVendor,
      return_date: returnDate,
      source_entry_id: validRows[0]?.source_entry_id || null,
      debit_note_number: debitNoteNumber || null,
      items: Object.values(itemsMap),
      notes: returnNotes || null,
      freight_charges: parseFloat(freightCharges) || 0,
    };

    setSaving(true);
    setStockError(null);
    try {
      const forceParam = forceOverride ? '?force=true' : '';
      if (editPR) {
        await api.put(`/purchase-returns/${editPR.id}${forceParam}`, {
          return_date: returnDate,
          debit_note_number: debitNoteNumber || null,
          items: Object.values(itemsMap),
          notes: returnNotes || null,
          freight_charges: parseFloat(freightCharges) || 0,
        });
        toast.success(`Return ${editPR.return_number} updated! Stock adjusted.`);
        clearDraft();
        navigate(`/purchase-return/${editPR.id}`);
      } else {
        const res = await api.post(`/purchase-returns${forceParam}`, payload);
        toast.success(`Return ${res.data.return_number} created! Debit Note: ${debitNoteNumber}`);
        clearDraft();
        navigate('/purchase-returns');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.type === 'insufficient_stock') {
        setStockError(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save return');
      }
    } finally { setSaving(false); }
  };

  const { grandQty, grandAmount } = computeTotals(rows);

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="create-pr-page">
      <PRHeader editPR={editPR} saving={saving} onCancel={() => navigate('/purchase-returns')} onSave={() => submitReturn(false)} />

      <StockErrorBanner
        stockError={stockError}
        onOverride={() => { setStockError(null); submitReturn(true); }}
        onDismiss={() => setStockError(null)}
      />

      <VendorDetailsCard
        editPR={editPR} vendorRef={vendorRef}
        vendorSearch={vendorSearch} setVendorSearch={setVendorSearch} setSelectedVendor={setSelectedVendor}
        vendorDropdownOpen={vendorDropdownOpen} setVendorDropdownOpen={setVendorDropdownOpen}
        filteredVendors={filteredVendors} selectVendor={selectVendor}
        returnDate={returnDate} setReturnDate={setReturnDate}
        debitNoteNumber={debitNoteNumber} setDebitNoteNumber={setDebitNoteNumber}
        returnNotes={returnNotes} setReturnNotes={setReturnNotes}
      />

      <PRScannerCard scannerActive={scannerActive} startScanner={startScanner} stopScanner={stopScanner} scannerContainerId={scannerContainerId} />

      <ReturnTable
        groupedBoxRows={groupedBoxRows} hasMultipleBoxes={hasMultipleBoxes} rows={rows}
        updateRow={updateRow} selectDesign={selectDesign} removeRow={removeRow} addRow={addRow}
        filteredDesigns={filteredDesigns} handleTableKeyDown={handleTableKeyDown}
        breakPopupIdx={breakPopupIdx} setBreakPopupIdx={setBreakPopupIdx}
        handleBreak={handleBreak} handleUndoBreak={handleUndoBreak} currentBoxNumber={currentBoxNumber}
        grandQty={grandQty} grandAmount={grandAmount}
      />

      <FreightFooter freightCharges={freightCharges} setFreightCharges={setFreightCharges} />
    </div>
  );
};

export default CreatePurchaseReturn;
